import { Component, Element, Event, EventEmitter, h, Host, Prop, Watch } from '@stencil/core';
import {
  arrow,
  autoUpdate,
  computePosition,
  flip,
  hide,
  Middleware,
  Placement,
  shift,
  Strategy,
  getOverflowAncestors,
} from '@floating-ui/dom';
// @ts-ignore
import restrictScroll from 'restrict-scroll';
import { getRandomDOMId } from '../../utils/random';
import { TooltipType } from './types';

interface AhaTooltipBuildElements {
  tooltipElement: HTMLElement;
  triggerElement: HTMLElement;
  arrowElement: HTMLElement | null;
}

interface FloatingUiMiddlewaresMap {
  [key: string]: {
    activeIf: boolean;
    middleware: Middleware;
  };
}

export interface AhaTooltip extends HTMLAhaTooltipElement {}

/**
 * Hide all tooltips when clicking outside
 */
window.addEventListener('click', event => {
  const { target } = event;
  let activeTooltip: HTMLElement | null = null;
  if (target) {
    activeTooltip = document.querySelector(
      `#${(target as HTMLElement).closest('[aria-describedby]')?.getAttribute('aria-describedby')}`,
    );
  }
  // Get only the tooltips that are open, should be just one at a time.
  const allAhaTooltips = document.querySelectorAll('aha-tooltip[aria-hidden="false"]');
  if (allAhaTooltips.length > 0)
    Array.from(allAhaTooltips).forEach(element => {
      //If the element is the one the users is interacting (clicking) do nothing.
      if (activeTooltip !== element) element.setAttribute('open', 'false');
    });
});

/**
 * Custom tooltip component that uses [Floating Ui](https://floating-ui.com/) to position the tooltip and arrow.
 *
 * This is a descriptive tooltip, and it follows the WCAG 2.1 descriptive tooltip semantics.
 * The tooltip trigger is associated with the tooltip via `aria-describedby` and id.
 * The tooltip is unreachable when hidden via `aria-hidden`
 *
 * The association between trigger and tooltip is done automatically. On mount `aha-tooltip` generates an unique DOM ID for the association.
 * @slot default - Contents of the default slot.
 */
@Component({
  tag: 'aha-tooltip',
  styleUrl: 'aha-tooltip.css',
  shadow: true,
})
export class AhaTooltip {
  @Element() host!: HTMLAhaTooltipElement;

  private tooltipCleanup: null | (() => void) = null;

  private showDelayTimeout: null | number | undefined = undefined;
  private hideDelayTimeout: null | number | undefined = undefined;

  /**
   * Triggers before the `aha-tooltip` is visible
   */
  // eslint-disable-next-line @stencil/decorators-style
  @Event({ eventName: 'aha-tooltip:show', cancelable: true, bubbles: true })
  beforeShow!: EventEmitter<HTMLAhaTooltipElement>;

  /**
   * Triggers when the `aha-tooltip` is visible
   */
  // eslint-disable-next-line @stencil/decorators-style
  @Event({ eventName: 'aha-tooltip:shown', cancelable: true, bubbles: true })
  afterShow!: EventEmitter<HTMLAhaTooltipElement>;

  /**
   * Triggers before the `aha-tooltip` is not visible
   */
  // eslint-disable-next-line @stencil/decorators-style
  @Event({ eventName: 'aha-tooltip:hide', cancelable: true, bubbles: true })
  beforeHide!: EventEmitter<HTMLAhaTooltipElement>;

  /**
   * Triggers when the `aha-tooltip` is not visible
   */
  // eslint-disable-next-line @stencil/decorators-style
  @Event({ eventName: 'aha-tooltip:hidden', cancelable: true, bubbles: true })
  afterHide!: EventEmitter<HTMLAhaTooltipElement>;

  /**
   * DOM ID that defines the trigger of the tooltip
   */
  @Prop({ reflect: true, attribute: 'trigger' }) predefinedTrigger: string = '';
  /**
   * Whether to trigger or not the default click event of the `aha-tooltip` `trigger` slot
   */
  @Prop({ reflect: true, attribute: 'allow-default-click' }) allowDefaultClick: boolean = false;

  /**
   * Whether the `aha-tooltip` is open.
   */
  @Prop({ reflect: true, mutable: true }) open: boolean = false;

  /**
   * Whether `aha-tooltip` is disabled.
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * Whether to show the contents of `aha-tooltip` when it is clicked.
   */
  @Prop({ reflect: true, attribute: 'click-show' }) clickShow?: boolean = false;

  /**
   * Whether to hide the contents of `aha-tooltip` when it is clicked.
   */
  @Prop({ reflect: true, attribute: 'click-hide' }) clickHide?: boolean = false;

  /**
   * Whether to show the contents of `aha-tooltip` when it is focused.
   */
  @Prop({ reflect: true, attribute: 'focus-show' }) focusShow?: boolean = false;

  /**
   * Whether to hide the contents of `aha-tooltip` when it is blurred.
   */
  @Prop({ reflect: true, attribute: 'blur-hide' }) blurHide?: boolean = false;

  /**
   * Whether to show the contents of `aha-tooltip` when it is hovered.
   */
  @Prop({ reflect: true, attribute: 'hover-show' }) hoverShow: boolean = true;

  /**
   * Whether to hide the contents of `aha-tooltip` when it is hovered.
   */
  @Prop({ reflect: true, attribute: 'hover-hide' }) hoverHide: boolean = true;

  /**
   * Whether to enable or not the floating-ui flip middleware
   */
  @Prop({ reflect: true }) flip: boolean = true;

  /**
   * Whether to enable or not the floating-ui shift middleware
   */
  @Prop({ reflect: true }) shift: boolean = true;

  /**
   * Position strategy of the tooltip.
   */
  @Prop({ reflect: true }) strategy: Strategy = 'fixed';

  /**
   * Setter for the tooltip placement.
   */
  @Prop({ reflect: true }) placement: Placement = 'top';

  /**
   * Fallback placements for the tooltip.
   */
  @Prop({ attribute: 'fallback-placements' }) fallbackPlacements: Placement[] = [
    'top',
    'top-start',
    'top-end',
    'bottom',
    'bottom-start',
    'bottom-end',
    'right',
    'right-start',
    'right-end',
    'left',
    'left-start',
    'left-end',
  ];

  /**
   * Getter for the actual placement of the tooltip
   */
  // eslint-disable-next-line @stencil/decorators-style
  @Prop({ reflect: true, attribute: 'rendered-placement', mutable: true })
  renderedPlacement?: string;

  /**
   * Whether to enable the floating-ui arrow middleware.
   */
  @Prop({ reflect: true }) arrow: boolean = true;

  /**
   * Whether to enable the floating-ui hide middleware.
   */
  @Prop({ reflect: true, attribute: 'hide-tooltip' }) hideTooltip: boolean = true;

  /**
   * Controls the type of `aha-tooltip` to render.
   */
  @Prop({ reflect: true }) type?: TooltipType;

  /**
   * Whether the tooltip is an HTML popover. This will place the tooltip on the top layer. Only popover='manual' is supported.
   */
  @Prop({ reflect: true }) popover?: 'manual';

  /**
   * Whether to auto-close the tooltip when clicking outside or opening another tooltip
   */
  @Prop({ reflect: true, attribute: 'auto-close' }) autoClose: boolean = true;

  /**
   * Whether scrolling on parent elements bubbles the scroll event to those parents.
   */
  @Prop({ reflect: true, attribute: 'scroll-bubbles' }) scrollBubbles: boolean = true;

  /**
   * Delay in milliseconds before showing the tooltip.
   */
  @Prop({ reflect: true, attribute: 'show-delay' }) showDelay?: number = 300;

  /**
   * Delay in milliseconds before hiding the tooltip.
   */
  @Prop({ reflect: true, attribute: 'hide-delay' }) hideDelay?: number;

  // --------------------------------- WATCH PROPS --------------------------- //

  @Watch('open')
  watchOpenProp(open: boolean) {
    this.handleOpen(open);
  }

  @Watch('scrollBubbles')
  watchScrollBubblesProp(scrollBubbles: boolean) {
    if (scrollBubbles) restrictScroll.delete(this.host);
  }

  @Watch('placement')
  watchPlacementProp() {
    this.handlePropChange();
  }

  @Watch('flip')
  watchFlipProp() {
    this.handlePropChange();
  }

  @Watch('disabled')
  watchDisabledProp(disabled: boolean) {
    if (disabled) {
      this.open = false;
    }
  }

  // --------------------------------- END WATCH PROPS ------------------------- //

  // --------------------------------- METHODS --------------------------------- //

  /**
   * Map the order adn when to use each floating middleware
   */
  private floatingUiMiddlewares = (
    arrowElement: HTMLElement | null,
    tooltipElement: HTMLElement,
  ) => {
    const [scrollParent] = getOverflowAncestors(tooltipElement);
    const middlewaresMap: FloatingUiMiddlewaresMap = {
      shift: {
        activeIf: this.shift,
        middleware: shift(),
      },
      flip: {
        activeIf: this.flip,
        middleware: flip({ fallbackPlacements: this.fallbackPlacements }),
      },
      hide: {
        activeIf: this.hideTooltip,
        // @ts-expect-error this is based on maintainer's example https://github.com/floating-ui/floating-ui/issues/3108#issuecomment-2466055936
        middleware: hide({ boundary: scrollParent }),
      },
      arrow: {
        activeIf: Boolean(arrowElement && this.arrow),
        middleware: arrow({ element: arrowElement as HTMLElement, padding: 9 }),
      },
    };

    return Object.keys(middlewaresMap)
      .filter(key => middlewaresMap[key].activeIf)
      .map(key => middlewaresMap[key].middleware);
  };

  /**
   * Assign the position in the X and Y axis of the tooltip.
   */
  private handleTooltipPositioning = ({
    tooltipElement,
    x,
    y,
  }: {
    tooltipElement: HTMLElement;
    x: number;
    y: number;
  }) => {
    Object.assign(tooltipElement.style, {
      top: '0',
      left: '0',
      transform: `translate(${Math.round(x)}px,${Math.round(y)}px)`,
    });
  };

  private handleArrowPositioning = ({
    arrowElement,
    x,
    y,
    actualPlacement,
  }: {
    arrowElement: HTMLElement;
    x?: number;
    y?: number;
    actualPlacement: Placement;
  }) => {
    const staticSide: { [key: string]: string } = {
      top: 'bottom',
      right: 'left',
      bottom: 'top',
      left: 'right',
    };

    Object.assign(arrowElement.style, {
      left: x != null ? `${x}px` : '',
      top: y != null ? `${y}px` : '',
      [staticSide[actualPlacement.split('-')[0]]]: '0px',
    });
  };

  /**
   * Build the tooltip and apply the necessary middlewares depending on the configuration
   */
  private updateTooltip = ({
    tooltipElement,
    triggerElement,
    arrowElement,
  }: AhaTooltipBuildElements) => {
    tooltipElement.style.position = this.strategy;
    computePosition(triggerElement, tooltipElement, {
      placement: this.placement,
      strategy: this.strategy,
      middleware: this.floatingUiMiddlewares(arrowElement, tooltipElement),
    }).then(({ x, y, middlewareData, placement }) => {
      // Update renderedPlacement with actual placement.
      this.renderedPlacement = placement;
      // Handle tooltip position
      this.handleTooltipPositioning({ tooltipElement, x, y });
      // Handle arrow position
      if (arrowElement && this.arrow) {
        this.handleArrowPositioning({
          arrowElement,
          x: middlewareData.arrow?.x,
          y: middlewareData.arrow?.y,
          actualPlacement: placement,
        });
      }
      if (this.hideTooltip && middlewareData.hide) {
        Object.assign(tooltipElement.style, {
          visibility: middlewareData.hide.referenceHidden ? 'hidden' : 'visible',
        });
      }
    });
  };

  private buildTooltip = ({
    tooltipElement,
    triggerElement,
    arrowElement,
  }: AhaTooltipBuildElements) => {
    // Clean the tooltip before building it again.
    this.cleanTooltip();
    // Use autoUpdate to update the tooltip position on scroll and resize.
    this.tooltipCleanup = autoUpdate(triggerElement, tooltipElement, () =>
      this.updateTooltip({ tooltipElement, triggerElement, arrowElement }),
    );
  };

  private cleanTooltip = () => {
    if (this.tooltipCleanup) {
      this.tooltipCleanup();
    }
  };

  private hideAllButThis = () => {
    // Close other aha-tooltips but this
    const allAhaTooltips = document.querySelectorAll('aha-tooltip');
    Array.from(allAhaTooltips).forEach(element => {
      if (element !== this.host && !element.contains(this.host)) {
        element.setAttribute('open', 'false');
      }
    });
  };

  private get triggerElement() {
    let trigger = this.host.previousElementSibling as HTMLElement;
    if (this.predefinedTrigger) {
      trigger = document.querySelector(`#${this.predefinedTrigger}`) as HTMLElement;
    }
    return trigger;
  }

  private getTooltipElements = (): AhaTooltipBuildElements | null => {
    if (!this.host || !this.host.shadowRoot) return null;
    const tooltipElement = this.host.shadowRoot.querySelector<HTMLElement>('#tooltip');
    const triggerElement = this.triggerElement;
    const arrowElement = this.host.shadowRoot.querySelector<HTMLElement>('#tooltip-arrow');
    if (!tooltipElement || !triggerElement) return null;
    return {
      tooltipElement,
      triggerElement,
      arrowElement,
    };
  };

  private handlePropChange = () => {
    const tooltipElements = this.getTooltipElements();
    if (!tooltipElements) return;

    requestAnimationFrame(() => {
      this.buildTooltip(tooltipElements);
    });
  };

  private toggleTriggerOpenCssClass() {}

  private handleOpen = (open: boolean) => {
    const tooltipElements = this.getTooltipElements();
    if (!tooltipElements) return;

    const openChildTriggers = this.host.querySelectorAll(
      '.SelectroV2__dropdown.SelectroV2__dropdown--open',
    );
    if (openChildTriggers.length > 0) this.open = false;

    // Emit before hide/show event.
    this[open ? 'beforeShow' : 'beforeHide'].emit(this.host);
    tooltipElements.triggerElement.classList.toggle('tooltip-open', open);
    if (open) {
      // Disable scroll when aha-tooltip is open
      if (!this.scrollBubbles) restrictScroll.add(this);
      this.buildTooltip(tooltipElements);
      this.hideAllButThis();
      tooltipElements.tooltipElement.style.display = 'block';
    } else {
      // Clean tooltip and enable scroll when aha-tooltip is closed
      this.cleanTooltip();
      restrictScroll.delete(this.host);
      tooltipElements.tooltipElement.style.display = 'none';
    }
    // Emit after hide/show event.
    this[open ? 'afterShow' : 'afterHide'].emit(this.host);
  };

  /**
   * Single method to handle both show and hide of the aha-tooltip.
   * It receives an object with two properties that customizes if the
   * toggle should try to open and close everytime it's executed.
   * Both are true by default.
   */
  private toggleOpen({
    shouldOpen = true,
    shouldClose = true,
  }: {
    shouldOpen?: boolean;
    shouldClose?: boolean;
  }) {
    if (this.showDelayTimeout) clearTimeout(this.showDelayTimeout);
    if (this.hideDelayTimeout) clearTimeout(this.hideDelayTimeout);

    if (shouldOpen && !this.open) {
      this.showDelayTimeout = window.setTimeout(() => {
        if (this.host.hasAttribute('popover') && this.host.isConnected) {
          this.host.showPopover?.();
        }

        return (this.open = true);
      }, this.showDelay || 0);
    }
    if (shouldClose && this.open) {
      this.hideDelayTimeout = window.setTimeout(() => {
        if (this.host.hasAttribute('popover') && this.host.isConnected) {
          this.host.hidePopover?.();
        }

        return (this.open = false);
      }, this.hideDelay || 0);
    }
  }

  private initializeTrigger(trigger: HTMLElement) {
    const uniqueDOMId = getRandomDOMId('aha-tooltip-id');
    trigger.setAttribute('aria-describedby', uniqueDOMId);
    this.host.setAttribute('id', uniqueDOMId);
    // Add events to the aha-tooltip parent element.
    trigger.addEventListener('click', this.handleOnClick);
    trigger.addEventListener('mouseenter', this.handleOnMouseEnter);
    trigger.addEventListener('focus', this.handleOnFocus);
    trigger.addEventListener('mouseleave', this.handleOnMouseLeave);
    trigger.addEventListener('blur', this.handleOnBlur);
    trigger.addEventListener('keydown', this.handleOnKeydown);
  }

  // --------------------------------- METHODS --------------------------------- //

  // --------------------------------- EVENTS HANDLERS --------------------------------- //

  /**
   * Handles the onClick event.
   * It would try to open/close the tooltip if this.clickShow and this.clickHide are true or not.
   */
  private handleOnClick = (event?: MouseEvent) => {
    // Stop propagation of the event, so it won't trigger if it's a nested aha-tooltip.
    // event?.stopPropagation();
    // Check if click event needs to be handled.
    if (this.disabled || (!this.clickShow && !this.clickHide && this.type !== 'popover')) {
      return;
    }

    // If the currentTarget is an <aha-menu>, do nothing, as we don't want to trigger the tooltip if we are trying to open a menu.
    const currentTarget = event?.currentTarget as HTMLElement | undefined;
    if (currentTarget && currentTarget.tagName === 'AHA-MENU') return;

    // Check if clickShow or clickHide needs to be handled.
    this.toggleOpen({
      shouldOpen: this.clickShow || this.type === 'popover',
      shouldClose: this.clickHide || this.type === 'popover',
    });
  };

  /**
   * Handles the onMouseEnter event.
   * It would try to open the aha-tooltip if this.hoverShow is set to true.
   */
  private handleOnMouseEnter = () => {
    if (this.disabled) return;
    this.toggleOpen({ shouldOpen: this.hoverShow, shouldClose: false });
  };

  /**
   * Handles the onMouseLeave event.
   * It would try to close the aha-tooltip if this.hoverHide is set to true.
   */
  private handleOnMouseLeave = () => {
    if (this.disabled) return;
    this.toggleOpen({ shouldOpen: false, shouldClose: this.hoverHide });
  };

  /**
   * Handles the onFocus event.
   * It would try to open the aha-tooltip if this.focusShow is set to true.
   */
  private handleOnFocus = () => {
    if (this.disabled) return;
    this.toggleOpen({
      shouldOpen: this.focusShow || this.hoverShow,
      shouldClose: false,
    });
  };

  /**
   * Handles the onBlur event.
   * It would try to close the aha-tooltip if this.blurHide is set to true.
   */
  private handleOnBlur = () => {
    if (this.disabled) return;
    this.toggleOpen({
      shouldOpen: false,
      shouldClose: this.blurHide || this.hoverHide,
    });
  };

  /**
   * Handles the onKeyDown event.
   */
  private handleOnKeydown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (this.disabled) return;
    switch (event.key) {
      // Enter is enable when clickShow/clickHide are enabled.
      case 'Enter':
        this.toggleOpen({
          shouldOpen: this.clickShow || this.type === 'popover',
          shouldClose: this.clickHide || this.type === 'popover',
        });
        break;
      // Escape is enable by default, and it would try to close it when pressed.
      case 'Escape':
        this.toggleOpen({ shouldOpen: false, shouldClose: true });
        break;
      default:
        break;
    }
  };

  // ------------------------------- END EVENTS HANDLERS ------------------------------- //

  // ------------------------------- COMPONENT LIFECYCLE ------------------------------- //

  connectedCallback() {
    // If there isn't a trigger element don't initialize the trigger.
    if (!this.triggerElement) return;
    this.initializeTrigger(this.triggerElement);
  }

  componentDidLoad() {
    requestAnimationFrame(() => {
      this.handleOpen(this.open);
    });
  }

  disconnectedCallback() {
    // Clean the tooltip when the component is disconnected from the DOM.
    this.cleanTooltip();
  }

  // ------------------------------- END COMPONENT LIFECYCLE --------------------------- //

  render() {
    return (
      <Host aria-hidden={`${!this.open}`}>
        <div id="tooltip">
          <div id="tooltip__inner">
            <div id="tooltip__content">
              <slot></slot>
            </div>
            <div id="tooltip-arrow"></div>
          </div>
        </div>
      </Host>
    );
  }
}
